import secureApiClient from './api';
import { v4 as uuidv4 } from 'uuid';

export interface LexMessage {
  sessionId?: string;
  message: string;
  context?: Record<string, any>;
}

export interface LexResponse {
  message: string;
  context: {
    sessionId: string;
    intent?: string;
    entities?: Record<string, any>;
    conversationState?: Record<string, any>;
    currentStep?: string;
  };
  step?: string;
  data?: Record<string, any>;
  progress?: number;
  actions?: Array<{
    type: string;
    data: any;
  }>;
  suggestions?: string[];
  metadata?: {
    processingTime?: number;
    confidence?: number;
    source?: string;
    workflowId?: string;
    shouldIntervene?: boolean;
    // Propriedades específicas do fluxo de registro
    showLoginButton?: boolean;
    emailExists?: boolean;
    registrationStep?: string;
    errorType?: string;
    showNewEmailOption?: boolean;
    // Sistema híbrido de knowledge
    knowledgeSystem?: {
      source: 'template' | 'cache' | 'knowledge_base' | 'llm_fallback';
      documentsFound: number;
      relevanceScore: number;
      foundInCache: boolean;
      usedKnowledge: boolean;
      savedApiCall: boolean;
    };
  };
}

export interface OnboardingStatus {
  userId: string;
  stage: 'registration' | 'setup' | 'learning' | 'active';
  completedSteps: string[];
  currentStep?: string;
  progress: number;
  lastInteraction: Date;
}

class LexService {
  private sessionId: string;

  constructor() {
    this.sessionId = this.getOrCreateSessionId();
  }

  private getOrCreateSessionId(): string {
    const stored = localStorage.getItem('lex_session_id');
    if (stored) return stored;
    
    const newSessionId = uuidv4();
    localStorage.setItem('lex_session_id', newSessionId);
    return newSessionId;
  }

  async sendMessage(message: string, context?: Record<string, any>): Promise<LexResponse> {
    try {
      const response = await secureApiClient.post<{ success: boolean; data: LexResponse }>(
        '/api/lex/message',
        {
          sessionId: this.sessionId,
          message,
          context: {
            ...context,
            currentPage: window.location.pathname,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString()
          }
        }
      );
      
      return response.data.data;
    } catch (error) {
      console.error('Error sending message to LEX:', error);
      throw error;
    }
  }

  async chat(message: string, conversationHistory?: any[]): Promise<any> {
    try {
      const response = await secureApiClient.post('/api/lex/chat', {
        message,
        conversationHistory,
        context: {
          sessionId: this.sessionId
        }
      });
      
      return response.data.data;
    } catch (error) {
      console.error('Error chatting with LEX:', error);
      throw error;
    }
  }

  // Método específico para páginas públicas (registro, landing, etc)
  async chatPublic(message: string, context?: Record<string, any>): Promise<LexResponse> {
    try {
      return await this.sendMessage(message, {
        ...context,
        isPublicPage: true,
        pageType: 'registration'
      });
    } catch (error) {
      console.error('Error in public chat with LEX:', error);
      throw error;
    }
  }

  async getOnboardingStatus(): Promise<OnboardingStatus> {
    try {
      const response = await secureApiClient.get<{ success: boolean; data: OnboardingStatus }>(
        '/api/lex/onboarding/status'
      );
      
      return response.data.data;
    } catch (error) {
      console.error('Error getting onboarding status:', error);
      throw error;
    }
  }

  async updateOnboardingProgress(step: string, completed: boolean): Promise<OnboardingStatus> {
    try {
      const response = await secureApiClient.post<{ success: boolean; data: OnboardingStatus }>(
        '/api/lex/onboarding/progress',
        { step, completed }
      );
      
      return response.data.data;
    } catch (error) {
      console.error('Error updating onboarding progress:', error);
      throw error;
    }
  }

  async getUserContext(userId?: string): Promise<any> {
    try {
      const url = userId ? `/api/lex/context/${userId}` : '/api/lex/context';
      const response = await secureApiClient.get(url);
      
      return response.data.data;
    } catch (error) {
      console.error('Error getting user context:', error);
      throw error;
    }
  }

  getSessionId(): string {
    return this.sessionId;
  }

  async deleteRegistrationSession(sessionId: string): Promise<boolean> {
    try {
      const response = await secureApiClient.delete<{ success: boolean; data: boolean }>(
        `/api/lex/session/${sessionId}`
      );
      
      return response.data.data;
    } catch (error) {
      console.error('Error deleting registration session:', error);
      return false;
    }
  }

  clearSession(): void {
    localStorage.removeItem('lex_session_id');
    this.sessionId = this.getOrCreateSessionId();
  }

  /**
   * Monitora performance do sistema híbrido LEX
   */
  logKnowledgeSystemPerformance(response: LexResponse): void {
    if (response.metadata?.knowledgeSystem) {
      const ks = response.metadata.knowledgeSystem;
      const processingTime = response.metadata.processingTime || 0;
      
      // Log informativo sobre performance
      if (ks.savedApiCall) {
        console.log(`🚀 LEX: Template response saved API call! (${processingTime}ms)`);
      } else if (ks.foundInCache) {
        console.log(`⚡ LEX: Cache hit improved response time (${processingTime}ms)`);
      } else if (ks.usedKnowledge) {
        console.log(`📚 LEX: Enhanced LLM with ${ks.documentsFound} KB documents (${processingTime}ms)`);
      } else {
        console.log(`🤖 LEX: Pure LLM fallback (${processingTime}ms)`);
      }
      
      // Armazenar estatísticas locais para debug (se necessário)
      const stats = JSON.parse(localStorage.getItem('lex_performance_stats') || '{}');
      stats[ks.source] = (stats[ks.source] || 0) + 1;
      localStorage.setItem('lex_performance_stats', JSON.stringify(stats));
    }
  }

  /**
   * Obtém estatísticas de performance acumuladas
   */
  getPerformanceStats(): Record<string, number> {
    return JSON.parse(localStorage.getItem('lex_performance_stats') || '{}');
  }

  /**
   * Limpa estatísticas de performance
   */
  clearPerformanceStats(): void {
    localStorage.removeItem('lex_performance_stats');
  }
}

export const lexService = new LexService();